import { useEffect, useRef } from 'react'

/**
 * Canonical Web Audio lookahead scheduler ("A Tale of Two Clocks",
 * Chris Wilson). The UI's `setInterval` tick runs every 25 ms and
 * schedules any steps that fall within the next 100 ms against the
 * AudioContext's own clock — that keeps playback sample-accurate
 * even when the main thread is busy re-rendering.
 */

interface ClockHandlers {
  /**
   * Called whenever a step boundary should fire. `stepIndex` wraps
   * modulo `stepsPerProject`. `when` is the AudioContext time the
   * step lands on, so the caller can schedule BufferSource.start(when)
   * with the same timestamp.
   */
  onStep: (stepIndex: number, when: number) => void
  /** Total steps in the loop (currently always 16 — one bar of 16ths). */
  stepsPerProject: number
}

interface ClockOptions {
  /** Beats per minute. */
  bpm: number
  /** Start/stop. Pausing resets the step pointer to 0 on next start. */
  running: boolean
  /**
   * AudioContext.currentTime supplier. Passed in so the hook doesn't
   * need to own the AudioContext — `useAudioEngine.currentTime`.
   */
  currentTime: () => number
  handlers: ClockHandlers
}

const LOOKAHEAD_MS = 25 // how often the JS tick polls
const SCHEDULE_AHEAD_SEC = 0.1 // how far ahead we queue audio events

export function useSequencerClock({
  bpm,
  running,
  currentTime,
  handlers,
}: ClockOptions) {
  // Stash handlers + bpm in refs so the interval callback doesn't
  // need to tear down every time a parent re-renders. Otherwise
  // toggling a step in the project would restart the scheduler.
  const handlersRef = useRef(handlers)
  const bpmRef = useRef(bpm)
  useEffect(() => {
    handlersRef.current = handlers
  }, [handlers])
  useEffect(() => {
    bpmRef.current = bpm
  }, [bpm])

  useEffect(() => {
    if (!running) return

    // 16th-note duration in seconds. Derived on every step so bpm
    // changes mid-playback take effect from the next scheduled step
    // without restarting the clock.
    const stepDuration = () => 60 / bpmRef.current / 4

    let stepIndex = 0
    let nextStepTime = currentTime() + 0.05 // tiny prime so the first hit isn't in the past

    const tick = () => {
      const ct = currentTime()
      // Fire every step whose time has arrived within the lookahead window.
      while (nextStepTime < ct + SCHEDULE_AHEAD_SEC) {
        const { onStep, stepsPerProject } = handlersRef.current
        onStep(stepIndex % stepsPerProject, nextStepTime)
        stepIndex = (stepIndex + 1) % stepsPerProject
        nextStepTime += stepDuration()
      }
    }

    // Prime the pump immediately, then maintain on an interval.
    tick()
    const id = window.setInterval(tick, LOOKAHEAD_MS)
    return () => window.clearInterval(id)
  }, [running, currentTime])
}
